import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Sparkles, Calendar } from 'lucide-react';
import { getCurrentSeason, getUpcomingEvents, getDaysUntil } from '../utils/liturgicalCalendar';

const SeasonalInspiration = ({ weeklyVerse }) => {
  const [season, setSeason] = useState(null);
  const [upcomingEvents, setUpcomingEvents] = useState([]);

  // Refresh season data - runs on mount and every hour
  useEffect(() => {
    const updateSeason = () => {
      const now = new Date();
      setSeason(getCurrentSeason(now));
      setUpcomingEvents(getUpcomingEvents(now, 3));
    };

    updateSeason();

    // Refresh every hour to catch day changes
    const interval = setInterval(updateSeason, 60 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const isSpecialSeason = season && season.id !== 'ordinary';
  
  const displayVerse = isSpecialSeason ? season.message.split(' - ')[0] : weeklyVerse?.verse;
  const displayReference = isSpecialSeason ? season.message.split(' - ')[1] : weeklyVerse?.reference;
  const displayTheme = isSpecialSeason ? season.name : weeklyVerse?.theme;

  if (!season && !weeklyVerse) return null;

  const getSeasonGradient = () => {
    if (!isSpecialSeason) return 'from-indigo-900 via-purple-900 to-slate-900';
    
    switch (season.id) {
      case 'advent': return 'from-purple-900 via-indigo-900 to-purple-900';
      case 'christmas': return 'from-red-900 via-green-900 to-red-900';
      case 'epiphany': return 'from-amber-900 via-yellow-800 to-amber-900';
      case 'ash_wednesday': return 'from-gray-900 via-purple-950 to-gray-900';
      case 'lent': return 'from-purple-950 via-violet-900 to-purple-950';
      case 'palm_sunday': return 'from-green-900 via-emerald-800 to-green-900';
      case 'holy_week': return 'from-purple-950 via-red-950 to-purple-950';
      case 'maundy_thursday': return 'from-purple-950 via-red-900 to-purple-950';
      case 'good_friday': return 'from-gray-900 via-slate-900 to-gray-900';
      case 'easter':
      case 'easter_monday': return 'from-amber-700 via-yellow-600 to-amber-700';
      case 'ascension': return 'from-sky-900 via-blue-800 to-sky-900';
      case 'pentecost': return 'from-red-900 via-orange-800 to-red-900';
      case 'trinity_sunday': return 'from-blue-900 via-indigo-900 to-blue-900';
      case 'corpus_christi': return 'from-amber-900 via-yellow-800 to-amber-900';
      case 'transfiguration': return 'from-sky-800 via-amber-700 to-sky-800';
      case 'assumption': return 'from-blue-900 via-sky-800 to-blue-900';
      case 'harvest_festival': return 'from-amber-900 via-orange-800 to-amber-900';
      case 'all_saints': return 'from-slate-800 via-amber-900 to-slate-800';
      case 'all_souls': return 'from-gray-900 via-purple-900 to-gray-900';
      case 'world_prayer_day': return 'from-teal-900 via-emerald-800 to-teal-900';
      default: return 'from-indigo-900 via-purple-900 to-slate-900';
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.2 }}
      data-testid="seasonal-inspiration"
      className={`relative overflow-hidden rounded-2xl bg-gradient-to-br ${getSeasonGradient()} p-4 sm:p-5 text-white shadow-lg`}
    >
      {/* Decorative elements */}
      <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full blur-2xl -translate-y-16 translate-x-16"></div>
      <div className="absolute top-1/2 right-4 text-[60px] text-white/5 font-serif select-none">"</div>
      
      <div className="relative z-10">
        <div className="flex flex-col sm:flex-row sm:items-start gap-4">
          {/* Left: Inspiration Content */}
          <div className="flex items-start gap-3 flex-1">
            <motion.div 
              className="p-2 rounded-lg bg-gradient-to-br from-amber-400 to-orange-500 flex-shrink-0"
              animate={{ rotate: [0, 5, -5, 0] }}
              transition={{ repeat: Infinity, duration: 4 }}
            >
              {isSpecialSeason ? (
                <span className="text-lg">{season.icon}</span>
              ) : (
                <Sparkles className="w-4 h-4 text-white" />
              )}
            </motion.div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <h3 className="text-sm font-bold text-white" data-testid="season-name">
                  {isSpecialSeason ? season.name : 'Weekly Inspiration'}
                </h3>
                {displayTheme && (
                  <span className="px-2 py-0.5 rounded-full bg-white/20 text-white/90 text-[10px] font-medium">
                    {isSpecialSeason ? season.subtitle : displayTheme}
                  </span>
                )}
              </div>
              <p className="text-base sm:text-lg font-medium text-white/95 italic leading-relaxed">
                "{displayVerse}"
              </p>
              <p className="mt-2 text-amber-400 font-semibold text-sm">— {displayReference}</p>
            </div>
          </div>

          {/* Right: Upcoming Events - ALWAYS visible */}
          {upcomingEvents.length > 0 && (
            <div className="flex items-center gap-2 sm:flex-col sm:items-end">
              <span className="text-white/60 text-xs flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                Coming:
              </span>
              <div className="flex gap-2 sm:flex-col">
                {upcomingEvents.slice(0, 2).map((event) => {
                  const daysAway = getDaysUntil(event.date);
                  return (
                    <div
                      key={event.name + event.date.toISOString()}
                      className="flex items-center gap-1.5 px-2 py-1 rounded-lg bg-white/15 text-xs"
                      data-testid={`upcoming-event-${event.name.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      <span>{event.season.icon}</span>
                      <div>
                        <p className="text-white font-medium text-[10px]">{event.name}</p>
                        <p className="text-white/60 text-[9px]">
                          {event.date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                          {daysAway <= 14 && (
                            <span className="ml-1 text-amber-300">
                              ({daysAway === 0 ? 'Today' : daysAway === 1 ? 'Tomorrow' : `${daysAway}d`})
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default SeasonalInspiration;
